import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Globe, Bell, Phone, Sparkles, Check } from 'lucide-react';
import { useOnboardingStore } from '@/stores/onboarding-store';
import { useContainerSize } from '@/hooks/use-container-size';

const QUICK_CODES = [
  { code: '1', label: 'US/CA', flag: '\u{1F1FA}\u{1F1F8}' },
  { code: '44', label: 'UK', flag: '\u{1F1EC}\u{1F1E7}' },
  { code: '91', label: 'India', flag: '\u{1F1EE}\u{1F1F3}' },
  { code: '61', label: 'Australia', flag: '\u{1F1E6}\u{1F1FA}' },
  { code: '55', label: 'Brazil', flag: '\u{1F1E7}\u{1F1F7}' },
  { code: '971', label: 'UAE', flag: '\u{1F1E6}\u{1F1EA}' },
];

/**
 * Full-page dialog for onboarding steps 4 (country code) and 5 (notification contact).
 * Only shows after step 3 (timeline) is completed.
 */
export function OnboardingPrompt() {
  const pendingStep = useOnboardingStore((s) => s.pendingPromptStep);
  const dismiss = useOnboardingStore((s) => s.dismissPromptStep);
  const defaultCountryCode = useOnboardingStore((s) => s.defaultCountryCode);
  const setDefaultCountryCode = useOnboardingStore((s) => s.setDefaultCountryCode);
  const notificationContact = useOnboardingStore((s) => s.notificationContact);
  const setNotificationContact = useOnboardingStore((s) => s.setNotificationContact);
  const { isCompact } = useContainerSize();
  const [saving, setSaving] = useState(false);

  if (!pendingStep) return null;
  // Country-code prompt is intentionally disabled — only the notification
  // (phone) step is surfaced. Trigger sites bypass 'country-code' directly,
  // but this guard protects against any stray setter.
  if (pendingStep === 'country-code') return null;

  const handleSave = async () => {
    setSaving(true);
    await dismiss();
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-background/80 backdrop-blur-sm">
      <AnimatePresence mode="wait">
        <motion.div
          key={pendingStep}
          initial={{ opacity: 0, scale: 0.95, y: 10 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: -10 }}
          transition={{ duration: 0.25, ease: [0.4, 0, 0.2, 1] }}
          className={`w-full ${isCompact ? 'max-w-xs mx-3' : 'max-w-md mx-4'}`}
        >
          <div className={`rounded-2xl border border-border bg-card shadow-2xl ${isCompact ? 'p-5' : 'p-8'}`}>
            {pendingStep === 'country-code' && (
              <div className="space-y-6">
                {/* Icon + Header */}
                <div className="text-center space-y-3">
                  <div className={`inline-flex items-center justify-center ${isCompact ? 'h-12 w-12' : 'h-14 w-14'} rounded-2xl bg-primary/10`}>
                    <Globe className={`${isCompact ? 'h-6 w-6' : 'h-7 w-7'} text-primary`} />
                  </div>
                  <div className="space-y-1">
                    <h2 className={`font-semibold text-foreground ${isCompact ? 'text-lg' : 'text-xl'}`}>
                      Default Country Code
                    </h2>
                    <p className={`text-muted-foreground leading-relaxed max-w-sm mx-auto ${isCompact ? 'text-xs' : 'text-sm'}`}>
                      Set the default country code for your customers. This is applied when sending messages to numbers without a country prefix.
                    </p>
                  </div>
                </div>

                {/* Input */}
                <div className="flex justify-center">
                  <div className="inline-flex items-center rounded-xl border-2 border-border bg-background px-4 py-3 focus-within:border-primary focus-within:ring-2 focus-within:ring-primary/20 transition-all">
                    <span className="text-lg font-semibold text-muted-foreground mr-1">+</span>
                    <input
                      value={defaultCountryCode}
                      onChange={(e) => setDefaultCountryCode(e.target.value.replace(/\D/g, '').slice(0, 4))}
                      placeholder="1"
                      className="w-16 bg-transparent text-lg font-semibold outline-none text-foreground placeholder:text-muted-foreground/30 text-center"
                      autoFocus
                    />
                  </div>
                </div>

                {/* Quick select */}
                <div className="space-y-2">
                  <p className="text-xs text-muted-foreground text-center">Quick select</p>
                  <div className="flex flex-wrap justify-center gap-2">
                    {QUICK_CODES.map((c) => (
                      <button
                        key={c.code}
                        onClick={() => setDefaultCountryCode(c.code)}
                        className={`inline-flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-xs font-medium transition-all ${
                          defaultCountryCode === c.code
                            ? 'border-primary bg-primary/10 text-primary'
                            : 'border-border bg-background text-foreground hover:border-primary/40 hover:bg-primary/5'
                        }`}
                      >
                        <span>{c.flag}</span>
                        <span>+{c.code}</span>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Actions */}
                <div className="flex items-center justify-between pt-2">
                  <button
                    onClick={handleSave}
                    className="text-sm text-muted-foreground hover:text-foreground transition-colors px-3 py-2"
                  >
                    Skip
                  </button>
                  <button
                    onClick={handleSave}
                    disabled={saving}
                    className="inline-flex items-center gap-2 rounded-xl bg-primary px-6 py-2.5 text-sm font-medium text-primary-foreground hover:bg-primary/90 transition-all shadow-md shadow-primary/20 disabled:opacity-50"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}

            {pendingStep === 'notification' && (
              <div className="space-y-6">
                {/* Icon + Header */}
                <div className="text-center space-y-3">
                  <div className={`inline-flex items-center justify-center ${isCompact ? 'h-12 w-12' : 'h-14 w-14'} rounded-2xl bg-primary/10`}>
                    <Bell className={`${isCompact ? 'h-6 w-6' : 'h-7 w-7'} text-primary`} />
                  </div>
                  <div className="space-y-1">
                    <h2 className={`font-semibold text-foreground ${isCompact ? 'text-lg' : 'text-xl'}`}>
                      Stay Notified
                    </h2>
                    <p className={`text-muted-foreground leading-relaxed max-w-sm mx-auto ${isCompact ? 'text-xs' : 'text-sm'}`}>
                      Enter your phone number to receive important app notifications and alerts via WhatsApp.
                    </p>
                  </div>
                </div>

                {/* Phone input */}
                <div className="flex items-stretch justify-center gap-2">
                  <div className="flex items-center rounded-xl border-2 border-border bg-background px-3 py-2.5 focus-within:border-primary transition-all">
                    <Phone className="h-4 w-4 text-muted-foreground mr-2 shrink-0" />
                    <input
                      value={notificationContact}
                      onChange={(e) => setNotificationContact(e.target.value.replace(/[^\d+]/g, ''))}
                      placeholder="+1 234 567 890"
                      className="w-40 bg-transparent text-sm outline-none text-foreground placeholder:text-muted-foreground/40"
                      autoFocus
                    />
                  </div>
                </div>

                {/* Info */}
                <div className="flex items-start gap-2 rounded-lg bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800 px-3 py-2">
                  <Sparkles className="h-3.5 w-3.5 text-blue-500 shrink-0 mt-0.5" />
                  <p className="text-xs text-blue-700 dark:text-blue-300 leading-relaxed">
                    We'll send you WhatsApp alerts for important events like failed deliveries, low credits, and new features.
                  </p>
                </div>

                {/* Actions */}
                <div className="flex items-center justify-between pt-2">
                  <button
                    onClick={handleSave}
                    className="text-sm text-muted-foreground hover:text-foreground transition-colors px-3 py-2"
                  >
                    Skip
                  </button>
                  <button
                    onClick={handleSave}
                    disabled={saving}
                    className="inline-flex items-center gap-2 rounded-xl bg-primary px-6 py-2.5 text-sm font-medium text-primary-foreground hover:bg-primary/90 transition-all shadow-md shadow-primary/20 disabled:opacity-50"
                  >
                    <Check className="h-4 w-4" />
                    Finish
                  </button>
                </div>
              </div>
            )}
          </div>
        </motion.div>
      </AnimatePresence>
    </div>
  );
}
